import {
  PageWrapper,
  PENDING_INTAKE_APPROVAL_DASHBOARD_PARAMS,
} from "@/components";
import DashboardTableFullView from "@/components/Dashboard/DashboardTableFullView";
import { pendingIntakeApprovalsFieldNames } from "@/enums";
import { getDeduplicatedDropdownOptions, routes } from "@/helpers";
import { useColumnFactory } from "@/hooks";
import { useGetPendingApprovalsQuery } from "@/services";
import { useIntl } from "react-intl";
import { useQueryParams } from "use-query-params";

const PAGINATION_PAGE_SIZE = 10;

const PendingIntakeApprovalsFullView = () => {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useQueryParams({
    ...PENDING_INTAKE_APPROVAL_DASHBOARD_PARAMS,
  });

  const {
    createIntakeViewLinkColumn,
    createCustomSortableSmallerNumColumn,
    createFilterableSortableDropdownColumn,
    createFilterableSortableNumberColumn,
    createFilterableCustomSortableNameColumn,
    createFilterableBooleanDropdownColumn,
    createFilterableSortableSmallerNameColumn,
    createSortableDateColumn,
    handleChange,
  } = useColumnFactory(queryParams, setQueryParams);

  const {
    data: { data: projectIntakesPendingApproval, totalCount } = {},
    isFetching,
  } = useGetPendingApprovalsQuery({
    ...queryParams,
    pageSize: PAGINATION_PAGE_SIZE,
  });

  return (
    <PageWrapper
      pageTitle={intl.formatMessage({
        id: "dashboard.titles.pendingIntakeApprovals",
      })}
    >
      <DashboardTableFullView
        data={projectIntakesPendingApproval}
        isFetching={isFetching}
        title={intl.formatMessage({
          id: "dashboard.titles.pendingIntakeApprovals",
        })}
        titleRoute={routes.dashboardFullViews.pendingIntakeApprovals}
        totalCount={totalCount}
        columns={[
          createIntakeViewLinkColumn(
            intl.formatMessage({ id: "dashboard.columns.intake" }),
            intl.formatMessage({ id: "dashboard.columns.approve" })
          ),
          createFilterableSortableNumberColumn(
            intl.formatMessage({ id: "dashboard.columns.projectNumber" }),
            pendingIntakeApprovalsFieldNames.projectNumber
          ),
          createFilterableCustomSortableNameColumn(
            intl.formatMessage({ id: "dashboard.columns.projectName" }),
            pendingIntakeApprovalsFieldNames.projectName,
            pendingIntakeApprovalsFieldNames.projectNameNormalized
          ),
          createFilterableSortableDropdownColumn(
            intl.formatMessage({ id: "dashboard.columns.projectType" }),
            pendingIntakeApprovalsFieldNames.projectType,
            pendingIntakeApprovalsFieldNames.projectTypeLabel,
            getDeduplicatedDropdownOptions(
              projectIntakesPendingApproval,
              pendingIntakeApprovalsFieldNames.projectType,
              pendingIntakeApprovalsFieldNames.projectTypeLabel
            )
          ),
          createFilterableSortableDropdownColumn(
            intl.formatMessage({ id: "dashboard.columns.productLine0" }),
            pendingIntakeApprovalsFieldNames.productLine0,
            pendingIntakeApprovalsFieldNames.productLineLabel0,
            getDeduplicatedDropdownOptions(
              projectIntakesPendingApproval,
              pendingIntakeApprovalsFieldNames.productLine0,
              pendingIntakeApprovalsFieldNames.productLineLabel0
            )
          ),
          createFilterableSortableDropdownColumn(
            intl.formatMessage({ id: "dashboard.columns.productLine1" }),
            pendingIntakeApprovalsFieldNames.productLine1,
            pendingIntakeApprovalsFieldNames.productLineLabel1,
            getDeduplicatedDropdownOptions(
              projectIntakesPendingApproval,
              pendingIntakeApprovalsFieldNames.productLine1,
              pendingIntakeApprovalsFieldNames.productLineLabel1
            )
          ),
          createFilterableSortableDropdownColumn(
            intl.formatMessage({ id: "dashboard.columns.productLine2" }),
            pendingIntakeApprovalsFieldNames.productLine2,
            pendingIntakeApprovalsFieldNames.productLineLabel2,
            getDeduplicatedDropdownOptions(
              projectIntakesPendingApproval,
              pendingIntakeApprovalsFieldNames.productLine2,
              pendingIntakeApprovalsFieldNames.productLineLabel2
            )
          ),
          createFilterableSortableDropdownColumn(
            intl.formatMessage({ id: "dashboard.columns.productCategory" }),
            pendingIntakeApprovalsFieldNames.productCategory,
            pendingIntakeApprovalsFieldNames.productCategoryLabel,
            getDeduplicatedDropdownOptions(
              projectIntakesPendingApproval,
              pendingIntakeApprovalsFieldNames.productCategory,
              pendingIntakeApprovalsFieldNames.productCategoryLabel
            )
          ),
          createFilterableSortableDropdownColumn(
            intl.formatMessage({
              id: "dashboard.columns.financeApprovalGroup",
            }),
            pendingIntakeApprovalsFieldNames.financeApprovalGroup,
            pendingIntakeApprovalsFieldNames.financeApprovalGroupLabel,
            getDeduplicatedDropdownOptions(
              projectIntakesPendingApproval,
              pendingIntakeApprovalsFieldNames.financeApprovalGroup,
              pendingIntakeApprovalsFieldNames.financeApprovalGroupLabel
            )
          ),
          createFilterableBooleanDropdownColumn(
            intl.formatMessage({
              id: "dashboard.columns.intangibleAccountingStandard",
            }),
            pendingIntakeApprovalsFieldNames.intangibleAccountingStandard
          ),
          createFilterableBooleanDropdownColumn(
            intl.formatMessage({
              id: "dashboard.columns.platformProject",
            }),
            pendingIntakeApprovalsFieldNames.platformProject
          ),
          createFilterableSortableSmallerNameColumn(
            intl.formatMessage({ id: "dashboard.columns.approver" }),
            pendingIntakeApprovalsFieldNames.approverName
          ),
          createSortableDateColumn(
            intl.formatMessage({ id: "dashboard.columns.dateAssigned" }),
            pendingIntakeApprovalsFieldNames.dateAssigned
          ),
          createCustomSortableSmallerNumColumn(
            intl.formatMessage({ id: "dashboard.columns.days" }),
            pendingIntakeApprovalsFieldNames.daysAvailable,
            pendingIntakeApprovalsFieldNames.dateAssigned
          ),
          createFilterableSortableSmallerNameColumn(
            intl.formatMessage({ id: "dashboard.columns.department" }),
            pendingIntakeApprovalsFieldNames.department
          ),
        ]}
        queryParams={queryParams}
        setQueryParams={setQueryParams}
        handleChange={handleChange}
        pageSize={PAGINATION_PAGE_SIZE}
      />
    </PageWrapper>
  );
};

export default PendingIntakeApprovalsFullView;
